import * as React from 'react';
import styled from 'styled-components';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { goBack, CallHistoryMethodAction, push } from 'connected-react-router';
import BackIcon from '@components/basics/icons/back';
import MotionOpacity from '@components/motion/motion-opacity';
import AppState from '@webapp/store/state';
import { createStripeCheckoutSession } from '@webapp/sdk/payment';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import { checkoutStartedMerchEvent } from '@webapp/utils/rudderstack/parseEvents';
import { loadStripe } from '@stripe/stripe-js';
import { CleanMerchStore } from '@webapp/store/merch/action-creators';
import {
  CreateSampleOrderSuccessResponse,
  ShippingDetails,
} from '@webapp/store/merch/state';
import SimpleArtist from '@common/types/artist/simple-artist';
import { setLocalStorageItem } from '@webapp/utils/localstorage';

interface MerchOrderPaymentPageProps {
  className?: string;
  order?: CreateSampleOrderSuccessResponse;
  shippingDetails?: ShippingDetails;
  artist?: SimpleArtist;
  goTo: (url: string) => CallHistoryMethodAction;
  goBack: () => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => ({
  artist: state.artist.active,
  shippingDetails: state.merch.active?.productSample?.shippingDetails,
  order: state.merch.active?.productSampleOrder,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goTo: (url: string) => dispatch(push(url)),
  goBack: () => dispatch(goBack()),
  cleanMerchStore: () => dispatch(new CleanMerchStore()),
});

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY as string
);

export const MerchOrderPaymentPage = (props: MerchOrderPaymentPageProps) => {
  const { order, shippingDetails, artist } = props;
  const [clientSecret, setClientSecret] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (order && artist) {
      checkoutStartedMerchEvent({ artist, order: order.order });
    }
  }, [artist, order]);

  React.useEffect(() => {
    if (!clientSecret && order && order.orderId) {
      createStripeCheckoutSession({
        line_items: order.order.products.map((p) => ({
          price_data: {
            currency: 'usd',
            unit_amount: p.price * 100,
            product_data: {
              name: p.title || '',
              description: p.description,
              images: p.size.image
                ? [p.size.image]
                : p.color.image
                ? [p.color.image]
                : [],
              tax_code: 'txcd_30011000',
            },
            tax_behavior: 'exclusive',
          },
          quantity: p.quantity,
        })),
        shipping_options: order.shippingOptions,
        customer_address: {
          line1: shippingDetails?.address,
          city: shippingDetails?.city,
          state: shippingDetails?.region,
          postal_code: shippingDetails?.postCode,
          country: shippingDetails?.country,
        },
        customer_shipping: {
          address: {
            line1: shippingDetails?.address,
            city: shippingDetails?.city,
            state: shippingDetails?.region,
            postal_code: shippingDetails?.postCode,
            country: shippingDetails?.country,
          },
          name: shippingDetails?.name || '',
        },
        customer: order.order.artistId,
        return_url: `${window.location.origin}/merch-order/confirmation`,
      }).then((data) => {
        setLocalStorageItem('stripeCheckoutSessionId', data.id);
        setLocalStorageItem('merchSampleOrder', order.order);
        setClientSecret(data.client_secret);
      });
    }
  }, [order, clientSecret, shippingDetails]);

  return (
    <Container className={props.className}>
      <BackIcon className="mb-50 " onClick={() => props.goBack()} />
      {clientSecret && (
        <div style={{ marginLeft: '30px', marginRight: '30px' }}>
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      )}
    </Container>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MerchOrderPaymentPage);

const Container = styled(MotionOpacity)`
  position: relative;
`;
